import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PawPrint, Plus, Pencil, CalendarCheck, History, BellRing, X, ShieldCheck, Camera } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FileImage } from "@/components/FileImage";
import { AccountIdBanner } from "@/components/AccountIdBanner";
import { PupModal } from "./PupModal";
import { PupHistoryModal } from "./PupHistoryModal";
import { OnboardingWizard } from "./OnboardingWizard";
import { api } from "@/lib/api";
import { fmtMonth, pupAge } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const dueLabel = (dueDate) => {
  const days = Math.round((new Date(dueDate) - new Date()) / 86400000);
  if (days === 0) return "due today";
  if (days > 0) return `due in ${days} ${days === 1 ? "day" : "days"}`;
  return `overdue by ${-days} ${-days === 1 ? "day" : "days"}`;
};

const stageLabel = (n) => {
  if (n.stage === "upcoming") return "due next month";
  if (n.stage === "due") return "due this month";
  if (n.stage === "overdue") return "overdue since last month";
  return dueLabel(n.due_date);
};

const MyPups = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [pups, setPups] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [onboardOpen, setOnboardOpen] = useState(false);

  const load = async () => {
    try {
      const [p, n] = await Promise.all([api.get("/pups"), api.get("/notifications")]);
      setPups(p.data);
      setNotifs(n.data);
      if (p.data.length === 0) setOnboardOpen(true);
    } catch {
      toast.error("Could not load your pups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dismissPupNotifs = async (pupId) => {
    try {
      await api.post("/notifications/read", { pup_id: pupId });
      setNotifs((prev) => ({ items: prev.items.filter((n) => n.pup_id !== pupId), unread: prev.unread }));
    } catch {
      /* silent */
    }
  };

  const uploadPupPhoto = async (pup, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "photo");
      await api.post(`/pups/${pup.pup_id}/documents`, fd);
      toast.success(`${pup.name}'s photo updated`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const idVerified = user.id_verified === "verified";
  const idNote =
    user.id_verified === "rejected"
      ? "ID couldn't be verified — re-upload to book"
      : user.photo_id
        ? "ID verification in progress"
        : "Upload your photo ID to book";

  const notifsByPup = {};
  notifs.items.forEach((n) => {
    if (n.pup_id) (notifsByPup[n.pup_id] = notifsByPup[n.pup_id] || []).push(n);
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-32">
        <p className="font-display text-xl text-navy">Loading your pack…</p>
      </div>
    );
  }

  return (
    <section data-testid="my-pups-page" className="pt-32 pb-28 lg:pt-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal y={14}>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
                <span className="h-px w-10 bg-bark" aria-hidden="true" />
                Hello, {user.name?.split(" ")[0] || "pup parent"}
              </p>
              <h1 className="mt-4 font-display text-4xl font-medium tracking-tight text-ink sm:text-5xl">
                My <em className="font-editorial italic text-bark">pups</em>
              </h1>
            </div>
            <button
              type="button"
              data-testid="book-service-button"
              onClick={() => (pups.length ? navigate("/account/book") : toast.info("Add your pup's profile first — it takes a minute."))}
              className="inline-flex items-center gap-2 rounded-full bg-bark px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <CalendarCheck className="h-4 w-4" aria-hidden="true" />
              Book a service
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.03}>
          <AccountIdBanner user={user} setUser={setUser} />
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              {pups.length} {pups.length === 1 ? "pup" : "pups"}
            </span>
          </div>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pups.map((pup) => (
              <div
                key={pup.pup_id}
                data-testid={`pup-card-${pup.name.toLowerCase()}`}
                onClick={() => { setEditing(pup); setModalOpen(true); }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") { setEditing(pup); setModalOpen(true); } }}
                className={`cursor-pointer rounded-3xl border border-ink/8 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_50px_-28px_rgba(15,23,42,0.3)] ${pup.archived ? "opacity-60 grayscale" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <div className="relative shrink-0">
                    {pup.photo_path ? (
                      <FileImage path={pup.photo_path} alt={pup.name} className="h-16 w-16 rounded-full object-cover ring-2 ring-bark-soft" />
                    ) : (
                      <span className="flex h-16 w-16 items-center justify-center rounded-full bg-bark-soft text-bark">
                        <PawPrint className="h-7 w-7" aria-hidden="true" />
                      </span>
                    )}
                    <label
                      data-testid={`pup-photo-${pup.name.toLowerCase()}`}
                      title={pup.photo_path ? "Change photo" : "Add a photo"}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute -bottom-1 -right-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-bark text-white shadow-md transition-colors duration-200 hover:bg-bark-dark"
                    >
                      <Camera className="h-3.5 w-3.5" aria-hidden="true" />
                      <input
                        data-testid={`pup-photo-input-${pup.name.toLowerCase()}`}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => uploadPupPhoto(pup, e)}
                      />
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`pup-history-${pup.name.toLowerCase()}`}
                      onClick={(e) => { e.stopPropagation(); setHistoryFor(pup); }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                    >
                      <History className="h-3.5 w-3.5" aria-hidden="true" />
                      History
                    </button>
                    <button
                      type="button"
                      data-testid={`pup-edit-${pup.name.toLowerCase()}`}
                      onClick={(e) => { e.stopPropagation(); setEditing(pup); setModalOpen(true); }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 px-4 py-2 text-xs font-semibold text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      Edit
                    </button>
                  </div>
                </div>
                <h3 className="mt-5 font-display text-2xl font-medium text-ink">{pup.name}</h3>
                <p className="text-sm text-slate-500">
                  {[pup.breed, pupAge(pup.date_of_birth), pup.weight_kg ? `${pup.weight_kg} kg` : null]
                    .filter(Boolean)
                    .join(" · ") || "Details pending"}
                </p>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold uppercase tracking-wider">
                  {pup.archived && <span data-testid={`pup-disabled-badge-${pup.name.toLowerCase()}`} className="rounded-full bg-red-100 px-3 py-1 text-red-600">Disabled</span>}
                  {pup.gender && <span className="rounded-full bg-cream-dark px-3 py-1 text-slate-600">{pup.gender}</span>}
                  <span className={`rounded-full px-3 py-1 ${pup.neutered_spayed ? "bg-emerald-50 text-emerald-700" : "bg-amberwarm/10 text-amberwarm"}`}>
                    {pup.neutered_spayed ? "Neutered/Spayed" : "Not neutered"}
                  </span>
                  <span className="rounded-full bg-cream-dark px-3 py-1 text-slate-600">
                    {(pup.documents || []).length} files
                  </span>
                </div>

                {!idVerified && !pup.archived && (
                  <p
                    data-testid={`pup-id-note-${pup.name.toLowerCase()}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-3 flex items-center gap-1.5 rounded-xl bg-amberwarm/10 px-3 py-2 text-xs font-medium text-amberwarm"
                  >
                    <ShieldCheck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {idNote}
                  </p>
                )}

                {(notifsByPup[pup.pup_id] || []).length > 0 && (
                  <div
                    data-testid={`pup-reminders-${pup.name.toLowerCase()}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-4 rounded-2xl border border-amberwarm/30 bg-amberwarm/5 p-3"
                  >
                    <div className="flex items-center justify-between">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-ink">
                        <BellRing className="h-3.5 w-3.5 text-bark" aria-hidden="true" />
                        Reminders &amp; alerts
                      </p>
                      <button
                        type="button"
                        data-testid={`dismiss-reminders-${pup.name.toLowerCase()}`}
                        onClick={() => dismissPupNotifs(pup.pup_id)}
                        aria-label="Dismiss reminders"
                        className="rounded-full p-1 text-slate-400 transition-colors duration-200 hover:bg-white hover:text-bark"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {(notifsByPup[pup.pup_id] || []).map((n) => (
                        <li key={n.notif_id} data-testid={`reminder-${n.notif_id}`} className="text-xs text-slate-600">
                          <span className="font-semibold text-ink">{n.kind}</span>
                          {" · "}
                          {n.due_date ? `${fmtMonth(n.due_date)} — ${stageLabel(n)}` : n.body}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            <button
              type="button"
              data-testid="add-pup-button"
              onClick={() => { setEditing(null); setModalOpen(true); }}
              className="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed border-ink/15 bg-white/50 text-navy transition-colors duration-200 hover:border-bark hover:text-bark"
            >
              <span className="rounded-full bg-bark-soft p-4 text-bark">
                <Plus className="h-6 w-6" aria-hidden="true" />
              </span>
              <span className="font-display text-lg font-medium">Add another pup</span>
              <span className="max-w-[220px] text-center text-xs text-slate-400">
                Profiles for every pup — details, vet info, vaccination records and photos
              </span>
            </button>
          </div>
        </Reveal>
      </div>

      <PupModal open={modalOpen} onClose={() => setModalOpen(false)} pup={editing} onSaved={() => load()} />
      <PupHistoryModal pup={historyFor} open={Boolean(historyFor)} onClose={() => setHistoryFor(null)} />
      <OnboardingWizard open={onboardOpen} onClose={() => setOnboardOpen(false)} onDone={() => load()} />
    </section>
  );
};

export default MyPups;
